import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Flex,
    Grid,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    FormControl,
    FormLabel,
    Input,
    Select,
    IconButton,
    GridItem,
    Center,
    Spinner,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { customAuthAxios } from '../../../utils/customAuthAxios';
import { getDjangoToken } from '../../../utils/instagram/services/apiService';

interface InstagramDmContent {
    id: number;
    name: string;
    type: string;
    valid_period?: {
        start_at: string;
        end_at: string;
    };
}

interface PackageContent {
    dm_content_id: number;
    order: number;
}

export const TableHeaderCell = ({ children, ...props }: any) => (
    <GridItem
        bg="gray.200"
        textAlign="left"
        py={2}
        fontWeight="bold"
        paddingLeft="10px"
        {...props}
    >
        {children}
    </GridItem>
);

const InstagramDmContents: React.FC = () => {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [dmContents, setDmContents] = useState<InstagramDmContent[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showPackageForm, setShowPackageForm] = useState<boolean>(false);
    const [packageName, setPackageName] = useState('');
    const [contents, setContents] = useState<PackageContent[]>([{ dm_content_id: 0, order: 1 }]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const fetchDmContents = async () => {
        try {
            setIsLoading(true);
            const djangoToken = await getDjangoToken();
            const response = await customAuthAxios.get(
                `/api/restaurants/${restaurantId}/instagram/dm/list_for_bot/`,
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );
            console.log('DM Contents Response:', response.data);
            setDmContents(response.data);
        } catch (error) {
            console.error('Failed to fetch DM contents:', error);
            toast({
                title: 'エラー',
                description: 'DMコンテンツの取得に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId) {
            fetchDmContents();
        }
    }, [restaurantId]);

    const handleDelete = async (contentId: number) => {
        if (!window.confirm('このDMコンテンツを削除してもよろしいですか？')) {
            return;
        }

        try {
            const djangoToken = await getDjangoToken();
            await customAuthAxios.delete(
                `/api/restaurants/${restaurantId}/instagram/dm/${contentId}/`,
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );
            toast({
                title: '成功',
                description: 'DMコンテンツを削除しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            fetchDmContents();
        } catch (error) {
            console.error('Failed to delete DM content:', error);
            toast({
                title: 'エラー',
                description: 'DMコンテンツの削除に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleAddContent = () => {
        setContents([
            ...contents,
            { dm_content_id: 0, order: contents.length + 1 }
        ]);
    };

    const handleRemoveContent = (index: number) => {
        const newContents = contents.filter((_, i) => i !== index);
        newContents.forEach((content, i) => {
            content.order = i + 1;
        });
        setContents(newContents);
    };

    const handleContentChange = (index: number, dmContentId: number) => {
        const newContents = [...contents];
        newContents[index].dm_content_id = dmContentId;
        setContents(newContents);
    };

    const handlePackageSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const djangoToken = await getDjangoToken();
            await customAuthAxios.post(
                `/api/restaurants/${restaurantId}/instagram/package/`,
                {
                    name: packageName,
                    contents: contents,
                },
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );

            toast({
                title: '成功',
                description: 'パッケージを作成しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setShowPackageForm(false);
            setPackageName('');
            setContents([{ dm_content_id: 0, order: 1 }]);
        } catch (error) {
            console.error('Failed to create package:', error);
            toast({
                title: 'エラー',
                description: 'パッケージの作成に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (showPackageForm) {
        return (
            <Box p={4} maxW="800px" mx="auto">
                <Card>
                    <CardHeader>
                        <Flex justify="space-between">
                            <Text fontWeight="bold" fontSize="lg">パッケージ作成</Text>
                            <Button
                                variant="outline"
                                onClick={() => setShowPackageForm(false)}
                            >
                                キャンセル
                            </Button>
                        </Flex>
                    </CardHeader>
                    <CardBody>
                        <form onSubmit={handlePackageSubmit}>
                            <VStack spacing={6} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel>パッケージ名</FormLabel>
                                    <Input
                                        value={packageName}
                                        onChange={(e) => setPackageName(e.target.value)}
                                        placeholder="パッケージ名を入力"
                                    />
                                </FormControl>

                                <Box>
                                    <Flex justify="space-between" align="center" mb={4}>
                                        <Text fontWeight="bold">DMコンテンツ</Text>
                                        <Button
                                            leftIcon={<FaPlus size="20px" />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={handleAddContent}
                                        >
                                            コンテンツを追加
                                        </Button>
                                    </Flex>

                                    <VStack spacing={4} align="stretch">
                                        {contents.map((content, index) => (
                                            <HStack key={index} spacing={4}>
                                                <Text width="40px">{index + 1}</Text>
                                                <Select
                                                    value={content.dm_content_id}
                                                    onChange={(e) => handleContentChange(index, Number(e.target.value))}
                                                    placeholder="DMコンテンツを選択"
                                                    flex={1}
                                                >
                                                    {dmContents.map((dmContent) => (
                                                        <option key={dmContent.id} value={dmContent.id}>
                                                            {dmContent.name} ({dmContent.type})
                                                        </option>
                                                    ))}
                                                </Select>
                                                <IconButton
                                                    aria-label="削除"
                                                    icon={<FaTrash size="20px" />}
                                                    colorScheme="red"
                                                    onClick={() => handleRemoveContent(index)}
                                                    isDisabled={contents.length === 1}
                                                />
                                            </HStack>
                                        ))}
                                    </VStack>
                                </Box>

                                <Button
                                    type="submit"
                                    colorScheme="blue"
                                    isLoading={isSubmitting}
                                    isDisabled={!packageName || contents.some(c => c.dm_content_id === 0)}
                                >
                                    作成
                                </Button>
                            </VStack>
                        </form>
                    </CardBody>
                </Card>
            </Box>
        );
    }

    return (
        <Box p={4} maxW="800px" mx="auto">
            <Card>
                <CardHeader>
                    <Flex justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">DMコンテンツ</Text>
                        <Button
                            as="a"
                            href="create"
                            rel="noopener noreferrer"
                            colorScheme="blue"
                        >
                            新規作成
                        </Button>
                    </Flex>
                </CardHeader>
                <CardBody>
                    <Grid templateColumns="repeat(4,1fr)" gap={2} backgroundColor="gray.100" w="100%" h="40px">
                        <TableHeaderCell>コンテンツ名</TableHeaderCell>
                        <TableHeaderCell>タイプ</TableHeaderCell>
                        <TableHeaderCell>有効期限</TableHeaderCell>
                        <TableHeaderCell></TableHeaderCell>
                    </Grid>
                    {isLoading ? (
                        <Center py={4}>
                            <Spinner  />
                        </Center>
                    ) : dmContents.length > 0 ? (
                        dmContents.map((content) => (
                            <Grid key={content.id} templateColumns="repeat(4,1fr)" gap={2} w="100%" py={2}>
                                <GridItem>{content.name}</GridItem>
                                <GridItem>{content.type}</GridItem>
                                <GridItem>
                                    {content.valid_period ? (
                                        `${formatDate(content.valid_period.start_at)} - ${formatDate(content.valid_period.end_at)}`
                                    ) : (
                                        '期限なし'
                                    )}
                                </GridItem>
                                <GridItem>
                                    <HStack spacing={2}>
                                        <Button size="sm" colorScheme="blue">
                                            編集
                                        </Button>
                                        <IconButton
                                            aria-label="削除"
                                            icon={<FaTrash size="20px" />}
                                            colorScheme="red"
                                            onClick={() => handleDelete(content.id)}
                                        />
                                    </HStack>
                                </GridItem>
                            </Grid>
                        ))
                    ) : (
                        <Center py={4}>
                            <Text>DMコンテンツがありません</Text>
                        </Center>
                    )}
                </CardBody>
            </Card>

            <Card marginY={"20px"}>
                <CardHeader>
                    <Flex justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">パッケージ</Text>
                        <Button
                            colorScheme="blue"
                            onClick={() => setShowPackageForm(true)}
                        >
                            新規作成
                        </Button>
                    </Flex>
                </CardHeader>
                <CardBody>
                    <Grid templateColumns="repeat(4,1fr)" gap={2} backgroundColor="gray.100" w="100%" h="40px">
                        <TableHeaderCell>パッケージ名</TableHeaderCell>
                        <TableHeaderCell>コンテンツ数</TableHeaderCell>
                        <TableHeaderCell>作成日</TableHeaderCell>
                        <TableHeaderCell></TableHeaderCell>
                    </Grid>
                </CardBody>
            </Card>
        </Box>
    );
};

export default InstagramDmContents; 